import { useCallback, useMemo, useState } from 'react'
import type { MouseEvent } from 'react'
import { usersApi } from '@/lib/api'
import type { UserProfile } from '@/lib/api'
import { canCustomizeOwnAvatarColor } from '@/lib/billing/tier'
import {
  AVATAR_PALETTE_COUNT,
  getAvatarPalette,
  getAvatarPaletteForSeed,
  seedToPaletteIndex,
  type AvatarPalette,
} from '@/lib/utils/avatar'

export interface OwnAvatarAppearanceProps {
  appearance?: 'themeTinted' | 'palette'
  bgColor?: string
  fgColor?: string
}

type OwnAvatarProfile = Pick<
  UserProfile,
  'avatarSeed' | 'email' | 'name' | 'subscriptionTier'
> | null | undefined

export function useOwnAvatarColor(
  profile: OwnAvatarProfile,
  refreshProfile: () => Promise<unknown>,
) {
  const canCustomizeAvatarColor = canCustomizeOwnAvatarColor(profile?.subscriptionTier)
  const avatarSeed = profile?.avatarSeed ?? profile?.email ?? profile?.name ?? 'user'
  const avatarPalette = useMemo(() => getAvatarPaletteForSeed(avatarSeed), [avatarSeed])
  const [shuffling, setShuffling] = useState(false)
  const [overridePalette, setOverridePalette] = useState<AvatarPalette | null>(null)

  const activePalette = overridePalette ?? avatarPalette

  const shuffleAvatar = useCallback(
    async (event?: MouseEvent) => {
      event?.stopPropagation()
      if (!canCustomizeAvatarColor || shuffling) {
        return
      }

      const currentIndex = seedToPaletteIndex(avatarSeed)
      const nextIndex = (currentIndex + 1) % AVATAR_PALETTE_COUNT
      const nextPalette = getAvatarPalette(nextIndex)
      const newSeed = `color-${nextIndex}-${Date.now()}`

      setShuffling(true)
      setOverridePalette(nextPalette)

      try {
        await usersApi.updateProfile({ avatarSeed: newSeed })
        await refreshProfile()
        setOverridePalette(null)
      } catch {
        setOverridePalette(null)
      } finally {
        setShuffling(false)
      }
    },
    [avatarSeed, canCustomizeAvatarColor, refreshProfile, shuffling],
  )

  const avatarAppearanceProps = useMemo((): OwnAvatarAppearanceProps => {
    if (!canCustomizeAvatarColor) {
      return { appearance: 'themeTinted' }
    }

    return {
      appearance: 'palette',
      bgColor: activePalette.bg,
      fgColor: activePalette.fg,
    }
  }, [activePalette.bg, activePalette.fg, canCustomizeAvatarColor])

  return {
    canCustomizeAvatarColor,
    shuffleAvatar,
    shuffling,
    avatarAppearanceProps,
  }
}
